/** What the employee's own activity actually is, derived from real tickets.
 *
 * Pure functions over the portal's ticket feed, so the home screen's summary
 * cards are arithmetic on data the API already returned rather than six more
 * requests — and so they can be unit-tested without a DOM.
 *
 * ## The rule these encode
 *
 * Every figure here is countable from `GET /api/portal/tickets/`. Nothing is
 * estimated and nothing is invented. Two cards the brief asked for are absent
 * for exactly that reason — Approvals and Assets have no portal API at all, so
 * a number for them would be fiction. They appear in the navigation with an
 * honest state instead; see `docs/PORTAL-BACKEND-GAPS.md`.
 *
 * "Awaiting IT" and "Pending my reply" are the two halves of an open ticket
 * from the requester's point of view, and the split is the single most useful
 * thing this screen can tell them: one of those piles is their job and the
 * other is ours.
 */
import type { TicketSummary } from '../lib/types'

/** Statuses that mean the ticket is done. Everything else is live. */
const FINISHED = new Set(['resolved', 'closed', 'cancelled'])

/** The one status that puts the ball in the requester's court. Named from the
 *  backend's own vocabulary rather than guessed — `STATUS_LABELS` renders this
 *  as "Awaiting your reply". */
const AWAITING_REQUESTER = 'pending_customer'

export function isOpen(ticket: TicketSummary): boolean {
  return !FINISHED.has(ticket.status)
}

export function needsMyReply(ticket: TicketSummary): boolean {
  return ticket.status === AWAITING_REQUESTER
}

export function awaitingIt(ticket: TicketSummary): boolean {
  return isOpen(ticket) && !needsMyReply(ticket)
}

/** A service request rather than a fault. `type` is the backend's own field. */
export function isRequest(ticket: TicketSummary): boolean {
  return ticket.type === 'request'
}

/** Resolved within the calendar month containing `now`, in the reader's zone.
 *
 *  Calendar month rather than a rolling 30 days because "this month" is what
 *  the card says, and a figure that disagrees with its own label is worse than
 *  no figure. `now` is injected so this is testable without freezing time.
 */
export function resolvedThisMonth(tickets: TicketSummary[], now: Date): TicketSummary[] {
  return tickets.filter((ticket) => {
    if (!ticket.resolved_at) return false
    const resolved = new Date(ticket.resolved_at)
    return (
      resolved.getFullYear() === now.getFullYear() && resolved.getMonth() === now.getMonth()
    )
  })
}

export interface ActivitySummary {
  open: number
  needsMyReply: number
  awaitingIt: number
  resolvedThisMonth: number
  requests: number
}

export function summarise(tickets: TicketSummary[], now: Date): ActivitySummary {
  return {
    open: tickets.filter(isOpen).length,
    needsMyReply: tickets.filter(needsMyReply).length,
    awaitingIt: tickets.filter(awaitingIt).length,
    resolvedThisMonth: resolvedThisMonth(tickets, now).length,
    requests: tickets.filter((ticket) => isRequest(ticket) && isOpen(ticket)).length,
  }
}

/** Most recently active first. `last_activity_at` is the newest case log the
 *  requester may see, so this sorts by "what moved", not "what was raised". */
export function byRecentActivity(tickets: TicketSummary[]): TicketSummary[] {
  return [...tickets].sort(
    (a, b) => new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime(),
  )
}

/* ------------------------------------------------------------------ search */

export interface SearchHit {
  kind: 'ticket' | 'article' | 'service'
  id: string
  title: string
  subtitle: string
  to: string
}

interface Searchable {
  tickets: TicketSummary[]
  articles: { slug: string; title: string }[]
  services: { id: number; name: string; service: string }[]
}

/** One box, three sources — the thing an employee actually wants from a search
 *  on a help portal: my ticket, the article that fixes it, or the request form.
 *
 *  Deliberately client-side over data already fetched. A server search endpoint
 *  would be better at scale, but none exists for the portal, and inventing one
 *  is out of scope; over a personal ticket list and a knowledge index this is
 *  both instant and honest about what it covers.
 */
export function search(query: string, data: Searchable, limit = 6): SearchHit[] {
  const needle = query.trim().toLowerCase()
  if (needle.length < 2) return []

  const matches = (haystack: string) => haystack.toLowerCase().includes(needle)
  const hits: SearchHit[] = []

  for (const ticket of data.tickets) {
    if (matches(ticket.title) || matches(ticket.ref)) {
      hits.push({
        kind: 'ticket',
        id: ticket.ref,
        title: ticket.title,
        subtitle: ticket.ref,
        to: `/tickets/${ticket.ref}`,
      })
    }
  }
  for (const article of data.articles) {
    if (matches(article.title)) {
      hits.push({
        kind: 'article',
        id: article.slug,
        title: article.title,
        subtitle: 'Knowledge article',
        to: `/kb/${article.slug}`,
      })
    }
  }
  for (const item of data.services) {
    if (matches(item.name) || matches(item.service)) {
      hits.push({
        kind: 'service',
        id: String(item.id),
        title: item.name,
        subtitle: item.service,
        to: `/catalog/${item.id}`,
      })
    }
  }

  // Articles first when the query looks like a problem rather than a reference:
  // somebody typing "outlook not syncing" wants the fix, not their old ticket.
  const order = { article: 0, service: 1, ticket: 2 } as const
  return hits.sort((a, b) => order[a.kind] - order[b.kind]).slice(0, limit)
}

/** Minutes to something an employee reads without doing arithmetic. */
export function humaniseMinutes(mins: number | null): string | null {
  if (mins === null || mins <= 0) return null
  if (mins < 60) return `${mins} minutes`
  const hours = mins / 60
  if (hours < 24) return hours === 1 ? '1 hour' : `${Number(hours.toFixed(1))} hours`
  const days = hours / 24
  return days === 1 ? '1 working day' : `${Number(days.toFixed(1))} working days`
}
